import UI from './ui-base';
import EventHandler from '../_dependencyvendor/event-handler';
import { dataSetToObject } from '../util/dom-util';

// eslint-disable-next-line no-unused-vars
const VERSION = '1.0.1';
const NAME = 'ui.tab';

const IDENTIFIER = {
  HEADER: 'data-tab-header',
  OBSERVER: 'data-tab-observer',
};

const dataAttrConfig = {
  init: true,
  activeClass: 'active',
  initialClass: 'ui-tab',
  active: 0,
  direction: 'horizontal',
};

const defaultConfig = {
  ...dataAttrConfig,
  delegate: {
    selectFilter: (els, el, className) => {
      return els[0].closest(className) === el.closest(className);
    },
    onAddClass: (state, current, activeClass) => {
      if (state === 'show') {
        current.header.classList.add(activeClass);
      } else {
        current.header.classList.remove(activeClass);
      }
    },
  },
  debug: false,
};

class Tab extends UI {
  constructor(element, config = {}) {
    super(element, config);
    this._currentActiveIndex = -1;
    this._tabTotal = 0;
    this._tabElements = {};
    this._activeTabInfo = null;
    this._beforeTabInfo = null;
    this._setupConfog(config);
    this._clearFunc = null;
    this._observer = null;
    this._observerElement = null;
    // 현재 UI가 즉시 초기화 가능 상태인지 체크 후 초기화 처리
    if (this._config.init === true || this._config.init === 'true') {
      this.init();
    }
  }

  static GLOBAL_CONFIG = {};

  static get EVENT() {
    return {
      CHANGE: `${NAME}.change`,
    };
  }

  static get NAME() {
    return NAME;
  }

  /**
   * 컴포넌트 초기화
   */
  init() {
    this._initVars();
    this._initEvents();
    this._initARIA();
    this.show(Number(this._config.active));
    if (this._config.debug === true) {
      console.warn(`${Tab.NAME}: initialized - [${this.id}]`);
    }
  }

  update() {
    this.destroy();
    this.init();
  }

  destroy(regain = true) {
    for (const key in this._tabElements) {
      if (Object.prototype.hasOwnProperty.call(this._tabElements, key)) {
        const { header } = this._tabElements[key];
        const { activeClass } = this._config;
        header.classList.remove(activeClass);
        EventHandler.off(header, this._eventName('keydown'));
        EventHandler.off(header, this._eventName('click'));
      }
    }
    this._clearFunc();
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this._config.debug === true) {
      console.warn(`${NAME}: destroyed - [${this._element}]`);
    }
    this._tabElements = {};
    this._activeTabInfo = null;
    this._beforeTabInfo = null;
    this._observerElement = null;
    if (regain === false) {
      // 인스턴스 까지 모두..
      this._config = null;
      super.destroy();
    }
  }

  /**
   * Tab Show
   * @param {number|string|object} target
   */
  show(target) {
    const { debug } = this._config;
    if (!isNaN(target)) {
      const info = this._getHeaderByIndex(target);
      if (info) {
        this._show(info);
      }
    } else {
      if (typeof target === 'string') {
        if (this._tabElements && this._tabElements[target]) {
          this._show(this._tabElements[target]);
        } else if (debug === true) {
          console.warn(`${Tab.NAME}: [${target}] does not match any content element!`);
        }
      } else {
        const dName = target.getAttribute(IDENTIFIER.HEADER);
        if (dName && this._tabElements) {
          this._show(this._tabElements[dName]);
        }
      }
    }
  }

  getCurrentIndex() {
    return this._currentActiveIndex;
  }

  getCurrentInfo() {
    return this._activeTabInfo;
  }

  _setupConfog(config) {
    this._config = {
      ...defaultConfig,
      ...Tab.GLOBAL_CONFIG,
      ...config,
      ...dataSetToObject(this._element, dataAttrConfig, 'tab'),
    };
  }

  _keyDownHandler(event) {
    const { direction } = this._config;
    switch (event.key) {
      case 'ArrowUp':
      case 'Up': {
        event.preventDefault();
        if (direction === 'horizontal') return;
        this._currentActiveIndex -= 1;
        if (this._currentActiveIndex < 0) {
          this._currentActiveIndex = this._tabTotal - 1;
        }
        break;
      }
      case 'ArrowDown':
      case 'Down': {
        event.preventDefault();
        if (direction === 'horizontal') return;
        this._currentActiveIndex += 1;
        if (this._currentActiveIndex > this._tabTotal - 1) {
          this._currentActiveIndex = 0;
        }
        break;
      }
      case 'ArrowRight':
      case 'Right': {
        event.preventDefault();
        if (direction === 'vertical') return;
        this._currentActiveIndex += 1;

        if (this._currentActiveIndex > this._tabTotal - 1) {
          this._currentActiveIndex = 0;
        }
        break;
      }
      case 'ArrowLeft':
      case 'Left': {
        event.preventDefault();
        if (direction === 'vertical') return;
        this._currentActiveIndex -= 1;
        if (this._currentActiveIndex < 0) {
          this._currentActiveIndex = this._tabTotal - 1;
        }
        break;
      }
      default: {
        // ...
      }
    }
    this.show(this._currentActiveIndex);
    for (const key in this._tabElements) {
      if (Object.prototype.hasOwnProperty.call(this._tabElements, key)) {
        const tab = this._tabElements[key];
        const { header, index } = tab;
        if (index === this._currentActiveIndex) {
          header.focus();
        }
      }
    }
  }

  _headerClickHandler(event) {
    event.preventDefault();
    const contentName = event.currentTarget.getAttribute(IDENTIFIER.HEADER);
    this._show(this._tabElements[contentName]);
  }

  _createTabInfo() {
    const { selectFilter } = this._config.delegate;
    const headers = this._element.querySelectorAll(`[${IDENTIFIER.HEADER}]`);
    const className = `.${this._config.initialClass}`;
    let index = 0;

    for (const header of headers) {
      if (selectFilter(headers, header, className)) {
        const controls = header.getAttribute(IDENTIFIER.HEADER);
        const content = document.querySelector(controls);
        const labelledby = header.getAttribute('id') || super._getRandomSerial();
        header.setAttribute('id', labelledby);
        if (!content) {
          super._throwError(`[${controls}] does not match any content element!`);
        }
        this._tabElements[controls] = {
          header,
          content,
          index,
          labelledby,
          controls,
        };
        index += 1;
      }
    }
    this._tabTotal = index;
    // clear function
    return () => {
      // 동적으로 추가되었던 속성을 모두 삭제해준다.
      for (const key in this._tabElements) {
        if (Object.prototype.hasOwnProperty.call(this._tabElements, key)) {
          const info = this._tabElements[key];
          if (info.content.getAttribute('id').indexOf('id_') > -1) {
            info.content.removeAttribute('id');
          }

          // if (info.content.getAttribute('aria-labelledby').indexOf('id_') > -1) {
          //   info.content.removeAttribute('aria-labelledby');
          // }
          if (info.header.getAttribute('id').indexOf('id_') > -1) {
            info.header.removeAttribute('id');
          }
        }
      }
    };
  }

  _initVars() {
    this._observerElement = this._element.querySelector(`[${IDENTIFIER.OBSERVER}]`);
    this._clearFunc = this._createTabInfo();
  }

  _initARIA() {
    const tablist = this._element.querySelector('[data-tab-list]');
    tablist.setAttribute('role', 'tablist');
    for (const key in this._tabElements) {
      if (Object.prototype.hasOwnProperty.call(this._tabElements, key)) {
        const tab = this._tabElements[key];
        tab.header.setAttribute('role', 'tab');
        tab.content.setAttribute('role', 'tabpanel');
        tab.header.setAttribute('aria-controls', tab.controls);
        tab.content.setAttribute('aria-labelledby', tab.labelledby);
        tab.header.setAttribute('aria-selected', 'false');
        tab.content.setAttribute('aria-hidden', 'false');
        tab.content.setAttribute('tabindex', '0');
        tab.content.style.display = 'none';
      }
    }
  }

  _initEvents() {
    for (const key in this._tabElements) {
      if (Object.prototype.hasOwnProperty.call(this._tabElements, key)) {
        const { header } = this._tabElements[key];
        EventHandler.on(header, this._eventName('keydown'), this._keyDownHandler.bind(this));
        EventHandler.on(header, this._eventName('click'), this._headerClickHandler.bind(this));
      }
    }
    if (this._observerElement) {
      this._observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            this.update();
          }
        });
      });
      this._observer.observe(this._observerElement, {
        childList: true,
      });
    }
  }

  _show(tabInfo) {
    this._activeTabInfo = tabInfo;
    const { onAddClass } = this._config.delegate;
    const { content, index } = this._activeTabInfo;
    this._hideBefore();
    content.style.display = 'block';
    onAddClass.apply(this, ['show', this._activeTabInfo, this._config.activeClass]);
    EventHandler.trigger(this._element, Tab.EVENT.CHANGE, {
      current: this._activeTabInfo,
      before: this._beforeTabInfo,
    });
    this._currentActiveIndex = index;
    this._beforeTabInfo = tabInfo;
    this._ariaControl(tabInfo);
  }

  _hideBefore() {
    if (this._beforeTabInfo) {
      const { onAddClass } = this._config.delegate;
      const { content } = this._beforeTabInfo;
      content.style.display = 'none';
      onAddClass.apply(this, ['hide', this._beforeTabInfo, this._config.activeClass]);
    }
  }

  _ariaControl(tab) {
    for (const key in this._tabElements) {
      if (Object.prototype.hasOwnProperty.call(this._tabElements, key)) {
        const element = this._tabElements[key];
        if (tab.index === element.index) {
          tab.header.setAttribute('aria-selected', 'true');
          tab.header.setAttribute('tabindex', '0');
          tab.content.setAttribute('aria-hidden', 'false');
        } else {
          element.header.setAttribute('aria-selected', 'false');
          element.header.setAttribute('tabindex', '-1');
          tab.content.setAttribute('aria-hidden', 'true');
        }
      }
    }
  }

  _getHeaderByIndex(index) {
    let r = null;
    for (const key in this._tabElements) {
      if (Object.prototype.hasOwnProperty.call(this._tabElements, key)) {
        const info = this._tabElements[key];
        const tabIndex = info.index;
        if (tabIndex === index) {
          r = info;
          break;
        }
      }
    }
    return r;
  }
}
export default Tab;
